import React from 'react';
import { Shield } from 'lucide-react';

const InsuranceSection = ({ insuranceProviders }) => {
  return (
    <section id="insurance" className="py-20 bg-gray-50" data-testid="insurance-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Accepted Insurance & Payment
            </h2>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Maryland Medicaid, Medicare, and self-pay accepted. Verify your coverage below or call us to confirm eligibility.
          </p>
        </div>

        {/* Insurance Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          {insuranceProviders.map((provider, index) => (
            <div
              key={provider.id}
              className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center hover:shadow-lg transition-shadow"
              data-testid={`insurance-card-${index}`}
            >
              <div className="text-center">
                <img
                  src={provider.logo_url}
                  alt={provider.name}
                  className="w-full h-16 object-contain mb-2"
                />
                <p className="text-sm font-medium text-gray-700">{provider.name}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Insurance Verification</h3>
            <p className="text-gray-600 mb-4">
              We'll verify your coverage before your first appointment to ensure you understand your benefits.
            </p>
            <button className="text-blue-600 font-medium hover:text-blue-700">
              Verify Coverage →
            </button>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Self-Pay Options</h3>
            <p className="text-gray-600 mb-4">
              Competitive self-pay rates and flexible payment plans available for those without insurance.
            </p>
            <button className="text-blue-600 font-medium hover:text-blue-700">
              View Pricing →
            </button>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Financial Assistance</h3>
            <p className="text-gray-600 mb-4">
              We offer sliding scale fees based on income for eligible individuals and families.
            </p>
            <button className="text-blue-600 font-medium hover:text-blue-700">
              Learn More →
            </button>
          </div>
        </div>

        {/* How Verification Works */}
        <div className="mt-16 max-w-3xl mx-auto text-center" data-testid="insurance-how-it-works">
          <h3 className="text-xl font-bold text-gray-900 mb-3">How Insurance Verification Works</h3>
          <p className="text-gray-600 mb-6">
            Before your first appointment, our intake team checks your plan details directly with your insurance
            carrier or Maryland HealthChoice MCO so you arrive knowing exactly what is covered. This typically takes
            1-2 business days and there is no cost or obligation to verify. If you are uninsured or your plan does
            not cover behavioral health services, we will walk you through self-pay rates and sliding-scale
            financial assistance so cost is never a barrier to getting care.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 text-left">
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs">1</span>
              <p className="text-sm text-gray-700">Share your insurance details during intake or by phone.</p>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs">2</span>
              <p className="text-sm text-gray-700">Our team confirms your benefits and any copay or deductible.</p>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs">3</span>
              <p className="text-sm text-gray-700">We confirm coverage with you before your first visit.</p>
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            Don't see your insurance listed? <a href="#contact" className="text-blue-600 font-medium hover:underline">Contact us</a> to verify coverage.
          </p>
        </div>
      </div>
    </section>
  );
};

export default InsuranceSection;